import React, { useEffect, useRef, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import { useUserContext, MeDto } from '../context/UserContext';
import { useApi } from '../hooks/useApi';

function gravatarUrl(email: string, size = 40) {
  const clean = email.trim().toLowerCase();
  return `https://www.gravatar.com/avatar/${hashEmail(clean)}?s=${size}&d=mp`;
}

function hashEmail(email: string): string {
  let h = 2166136261;
  for (let i = 0; i < email.length; i++) {
    h ^= email.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0).toString(16).padStart(8, '0');
}

interface Props {
  me: MeDto | null;
  onBilling: () => void;
  onMyLenses: () => void;
  showLenses?: boolean;
}

export function UserMenu({ me, onBilling, onMyLenses, showLenses = true }: Props) {
  const { logout } = useAuth0();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const email = me?.email ?? '';
  const avatarSrc = me?.photoUrl ?? (email ? gravatarUrl(email) : undefined);

  return (
    <>
      <div onClick={() => setOpen(o => !o)} style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', position: 'relative', cursor: 'pointer' }} ref={menuRef}>
        {me?.organization?.logoUrl && (
          <img src={me.organization.logoUrl} alt={me.organization.name} style={{ height: 36, objectFit: 'contain' }} />
        )}
        <span style={{ fontSize: 13, color: '#555', fontFamily: "'Montserrat', sans-serif" }}>{email}</span>
        {avatarSrc && (
          <img
            src={avatarSrc}
            alt=""
            style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', border: '1px solid #ddd' }}
          />
        )}

        {open && (
          <div style={{
            position: 'absolute', top: '110%', right: 0, background: '#fff',
            border: '1px solid #ddd', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
            minWidth: 180, zIndex: 1000, overflow: 'hidden',
            fontFamily: "'Montserrat', sans-serif",
          }}>
            {[
              { label: 'Lenses',   action: showLenses ? () => { setOpen(false); onMyLenses(); } : null },
              { label: 'Profile',  action: () => { setOpen(false); setProfileOpen(true); } },
              { label: 'Account',  action: () => { setOpen(false); navigate('/account'); } },
              { label: 'Billing',  action: () => { setOpen(false); onBilling(); } },
              { label: 'Log Out',  action: () => logout({ logoutParams: { returnTo: window.location.origin } }), danger: true },
            ].map(item => {
              const disabled = item.action === null;
              return (
                <button
                  key={item.label}
                  onClick={() => { if (item.action) item.action(); }}
                  disabled={disabled}
                  title={disabled ? 'Available with a paid subscription' : undefined}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '0.7rem 1.1rem', background: 'none', border: 'none',
                    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14,
                    color: disabled ? '#bbb' : (item as any).danger ? 'rgb(214,10,34)' : '#333',
                    borderBottom: !(item as any).danger ? '1px solid #f0f0f0' : 'none',
                    opacity: disabled ? 0.6 : 1,
                  }}
                  onMouseEnter={e => { if (!disabled) e.currentTarget.style.background = '#f8f8f8'; }}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  {item.label}
                  {disabled && <span style={{ fontSize: 11, color: '#ccc', marginLeft: '0.4rem' }}>— paid only</span>}
                </button>
              );
            })}
            {me?.isAdmin && (
              <>
                <div style={{ borderTop: '1px solid #e0e0e0', margin: '0.25rem 0' }} />
                <button
                  onClick={e => { e.stopPropagation(); setOpen(false); navigate('/admin'); }}
                  style={{
                    display: 'block', width: '100%', textAlign: 'left',
                    padding: '0.7rem 1.1rem', background: 'none', border: 'none',
                    cursor: 'pointer', fontSize: 14, color: '#333',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.background = '#f8f8f8'; }}
                  onMouseLeave={e => (e.currentTarget.style.background = 'none')}
                >
                  Admin
                </button>
              </>
            )}
          </div>
        )}
      </div>

      <ProfileSlider open={profileOpen} onClose={() => setProfileOpen(false)} />
    </>
  );
}

function ProfileSlider({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { me, refresh } = useUserContext();
  const { authFetch } = useApi();
  const { getAccessTokenSilently } = useAuth0();

  const [editing, setEditing] = useState(false);
  const [nameValue, setNameValue] = useState('');
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [editingOrgName, setEditingOrgName] = useState(false);
  const [orgNameValue, setOrgNameValue] = useState('');
  const [savingOrgName, setSavingOrgName] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const startEdit = () => {
    setNameValue(me?.displayName ?? '');
    setEditing(true);
  };

  const cancelEdit = () => setEditing(false);

  const saveName = async () => {
    setSaving(true);
    try {
      const res = await authFetch('/api/me/profile', {
        method: 'PUT',
        body: JSON.stringify({ displayName: nameValue || null }),
      });
      if (res.ok) {
        refresh();
        setEditing(false);
      }
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
      });
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_API_URL ?? ''}/api/me/photo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) refresh();
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const startEditOrgName = () => {
    setOrgNameValue(me?.organization?.name ?? '');
    setEditingOrgName(true);
  };

  const cancelEditOrgName = () => setEditingOrgName(false);

  const saveOrgName = async () => {
    if (!orgNameValue.trim()) return;
    setSavingOrgName(true);
    try {
      const res = await authFetch('/api/me/organization', {
        method: 'PUT',
        body: JSON.stringify({ name: orgNameValue.trim() }),
      });
      if (res.ok) {
        refresh();
        setEditingOrgName(false);
      }
    } finally {
      setSavingOrgName(false);
    }
  };

  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !me?.organization) return;
    setUploadingLogo(true);
    try {
      const token = await getAccessTokenSilently({
        authorizationParams: { audience: process.env.REACT_APP_AUTH0_AUDIENCE }
      });
      const formData = new FormData();
      formData.append('file', file);
      const res = await fetch(`${process.env.REACT_APP_API_URL ?? ''}/api/organizations/${me.organization.id}/logo`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (res.ok) refresh();
    } finally {
      setUploadingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  const email = me?.email ?? '';
  const photoSrc = me?.photoUrl ?? (email ? gravatarUrl(email, 80) : undefined);

  return (
    <>
      {open && (
        <div
          onClick={onClose}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.25)', zIndex: 1100 }}
        />
      )}
      <div style={{
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 320,
        background: '#fff', boxShadow: open ? '-4px 0 20px rgba(0,0,0,0.15)' : 'none',
        zIndex: 1200, transform: open ? 'translateX(0)' : 'translateX(100%)',
        transition: 'transform 0.25s ease', padding: '2rem 1.5rem',
        fontFamily: "'Montserrat', sans-serif",
        display: 'flex', flexDirection: 'column', gap: '1.25rem',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontSize: 18, fontWeight: 600 }}>Profile</span>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#888' }}>✕</button>
        </div>

        {/* Avatar — click to upload */}
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <div
            onClick={() => !uploading && fileInputRef.current?.click()}
            style={{ position: 'relative', cursor: uploading ? 'default' : 'pointer', width: 84, height: 84 }}
          >
            {photoSrc && (
              <img
                src={photoSrc}
                alt=""
                style={{ width: 80, height: 80, borderRadius: '50%', border: '2px solid #ddd', objectFit: 'cover', opacity: uploading ? 0.5 : 1 }}
              />
            )}
            {!uploading && (
              <div
                style={{
                  position: 'absolute', inset: 0, borderRadius: '50%', background: 'rgba(0,0,0,0)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  transition: 'background 0.15s',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.background = 'rgba(0,0,0,0.35)';
                  (e.currentTarget.querySelector('span') as HTMLElement).style.opacity = '1';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.background = 'rgba(0,0,0,0)';
                  (e.currentTarget.querySelector('span') as HTMLElement).style.opacity = '0';
                }}
              >
                <span style={{ color: '#fff', fontSize: 11, fontWeight: 600, pointerEvents: 'none', opacity: 0, transition: 'opacity 0.15s' }}>
                  Change
                </span>
              </div>
            )}
            {uploading && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #e0dbd7', borderTopColor: '#ba9394', animation: 'prism-spin 0.75s linear infinite' }} />
              </div>
            )}
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png"
            onChange={handlePhotoChange}
            style={{ display: 'none' }}
          />
        </div>

        {/* Name */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
            <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Name</div>
            {!editing && (
              <button
                onClick={startEdit}
                style={{ background: 'none', border: 'none', color: '#ba9394', cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: "'Montserrat', sans-serif" }}
              >
                Edit
              </button>
            )}
          </div>
          {editing ? (
            <div style={{ display: 'flex', gap: '0.4rem' }}>
              <input
                value={nameValue}
                onChange={e => setNameValue(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') cancelEdit(); }}
                autoFocus
                style={{ flex: 1, fontSize: 14, padding: '0.3rem 0.5rem', border: '1px solid #ddd', borderRadius: 4, fontFamily: "'Montserrat', sans-serif", outline: 'none' }}
              />
              <button
                onClick={saveName}
                disabled={saving}
                style={{ fontSize: 12, padding: '0.3rem 0.6rem', background: '#ba9394', color: '#fff', border: 'none', borderRadius: 4, cursor: saving ? 'default' : 'pointer', fontFamily: "'Montserrat', sans-serif", opacity: saving ? 0.6 : 1 }}
              >
                {saving ? '…' : 'Save'}
              </button>
              <button
                onClick={cancelEdit}
                style={{ fontSize: 12, padding: '0.3rem 0.6rem', background: 'none', color: '#888', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}
              >
                Cancel
              </button>
            </div>
          ) : (
            <div style={{ fontSize: 14, color: '#333' }}>{me?.displayName ?? '—'}</div>
          )}
        </div>

        {/* Email */}
        <div>
          <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '0.2rem' }}>Email</div>
          <div style={{ fontSize: 14, color: '#333' }}>{me?.email ?? '—'}</div>
        </div>

        {/* Organization */}
        {me?.organization && (
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.2rem' }}>
              <div style={{ fontSize: 11, color: '#999', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Organization</div>
              {me.isOrgManager && !editingOrgName && (
                <button
                  onClick={startEditOrgName}
                  style={{ background: 'none', border: 'none', color: '#ba9394', cursor: 'pointer', fontSize: 12, padding: 0, fontFamily: "'Montserrat', sans-serif" }}
                >
                  Edit
                </button>
              )}
            </div>
            {editingOrgName ? (
              <div style={{ display: 'flex', gap: '0.4rem' }}>
                <input
                  value={orgNameValue}
                  onChange={e => setOrgNameValue(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') saveOrgName(); if (e.key === 'Escape') cancelEditOrgName(); }}
                  autoFocus
                  style={{ flex: 1, fontSize: 14, padding: '0.3rem 0.5rem', border: '1px solid #ddd', borderRadius: 4, fontFamily: "'Montserrat', sans-serif", outline: 'none' }}
                />
                <button
                  onClick={saveOrgName}
                  disabled={savingOrgName || !orgNameValue.trim()}
                  style={{ fontSize: 12, padding: '0.3rem 0.6rem', background: '#ba9394', color: '#fff', border: 'none', borderRadius: 4, cursor: savingOrgName ? 'default' : 'pointer', fontFamily: "'Montserrat', sans-serif", opacity: savingOrgName ? 0.6 : 1 }}
                >
                  {savingOrgName ? '…' : 'Save'}
                </button>
                <button
                  onClick={cancelEditOrgName}
                  style={{ fontSize: 12, padding: '0.3rem 0.6rem', background: 'none', color: '#888', border: '1px solid #ddd', borderRadius: 4, cursor: 'pointer', fontFamily: "'Montserrat', sans-serif" }}
                >
                  Cancel
                </button>
              </div>
            ) : (
              <div style={{ fontSize: 14, color: '#333' }}>{me.organization.name || <span style={{ color: '#aaa' }}>—</span>}</div>
            )}

            <div
              onClick={() => me.isOrgManager && !uploadingLogo && logoInputRef.current?.click()}
              style={{ position: 'relative', marginTop: '0.5rem', width: '50%', cursor: me.isOrgManager && !uploadingLogo ? 'pointer' : 'default' }}
            >
              {me.organization.logoUrl ? (
                <img
                  src={me.organization.logoUrl}
                  alt={me.organization.name}
                  style={{ width: '100%', objectFit: 'contain', opacity: uploadingLogo ? 0.5 : 1 }}
                />
              ) : me.isOrgManager ? (
                <div style={{ fontSize: 12, color: '#aaa', border: '1px dashed #ddd', borderRadius: 4, padding: '0.75rem', textAlign: 'center' }}>
                  No logo
                </div>
              ) : null}
              {me.isOrgManager && !uploadingLogo && (
                <div
                  style={{ position: 'absolute', inset: 0, borderRadius: 4, background: 'rgba(0,0,0,0)', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s' }}
                  onMouseEnter={e => {
                    e.currentTarget.style.background = 'rgba(0,0,0,0.35)';
                    (e.currentTarget.querySelector('span') as HTMLElement).style.opacity = '1';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.background = 'rgba(0,0,0,0)';
                    (e.currentTarget.querySelector('span') as HTMLElement).style.opacity = '0';
                  }}
                >
                  <span style={{ color: '#fff', fontSize: 11, fontWeight: 600, pointerEvents: 'none', opacity: 0, transition: 'opacity 0.15s' }}>
                    {me.organization.logoUrl ? 'Change' : 'Upload'}
                  </span>
                </div>
              )}
              {uploadingLogo && (
                <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 20, height: 20, borderRadius: '50%', border: '2px solid #e0dbd7', borderTopColor: '#ba9394', animation: 'prism-spin 0.75s linear infinite' }} />
                </div>
              )}
            </div>
            {me.isOrgManager && (
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/png,image/svg+xml"
                onChange={handleLogoChange}
                style={{ display: 'none' }}
              />
            )}
          </div>
        )}
      </div>
    </>
  );
}
